import { useCallback, useState } from 'react';

import Text from '@ui/text/text/text';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectDisplaySessions, selectSessionFooter, selectWatchlist } from '@/store/slices/settings/selectors';
import SettingsToggle from '../../controls/settings-toggle';
import { getIsSettingsUpdating, getWorkingProjectUuid, selectProjectSettings } from '@/store/slices/project/selectors';
import { EventSettings } from '@/types/projects.types';
import { updateProjectSettings } from '@/store/slices/project';

const FeaturesWidget = () => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const projectSettings = useAppSelector(selectProjectSettings);
  const sessionFooterEnabled = useAppSelector(selectSessionFooter);
  const displaySessionsEnabled = useAppSelector(selectDisplaySessions);
  const watchlistEnabled = useAppSelector(selectWatchlist);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);

  const dispatch = useAppDispatch();

  const [loadingState, setLoadingState] = useState<Record<string, boolean>>({
    footer: false,
    watchTab: false,
    watchlist: false,
  });

  const handleToggle = useCallback(
    async (sKey: string, on: boolean) => {
      if (!projectUuid || !projectSettings || isSettingsUpdating) return;

      if (!sKey) {
        console.error(`Unknown setting key: ${sKey}`);
        return;
      }

      setLoadingState((prev) => ({ ...prev, [sKey]: true }));

      const settings: EventSettings = {
        ...projectSettings,
        [sKey]: on,
      };

      try {
        await dispatch(updateProjectSettings({ event_uuid: projectUuid, settings })).unwrap();
      } catch (error) {
        console.error(`Failed to update setting ${sKey}:`, error);
      } finally {
        setLoadingState((prev) => ({ ...prev, [sKey]: false }));
      }
    },
    [dispatch, isSettingsUpdating, projectSettings, projectUuid]
  );

  return (
    <>
      <div className="flex-column gap-8">
        <Text className="H5" color="var(--gray-100)">
          Navigation
        </Text>
        <SettingsToggle
          label="Footer"
          value={sessionFooterEnabled}
          sKey="display_session_footer"
          loading={loadingState['footer']}
          onToggle={handleToggle}
        />
        <SettingsToggle
          label="Watch tab in navigation"
          value={displaySessionsEnabled}
          sKey="display_sessions"
          loading={loadingState['watchTab']}
          onToggle={handleToggle}
        />
        <SettingsToggle
          label="Watchlist"
          value={watchlistEnabled}
          sKey="personalizedAttendeeList"
          loading={loadingState['watchlist']}
          onToggle={handleToggle}
        />
      </div>
    </>
  );
};

export default FeaturesWidget;
